import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
    consentForms: [],
    currentConsentForm: null,
    isLoading: false,
    error: null,
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all consent forms
export const fetchConsentForms = createAsyncThunk(
    'consent/fetchConsentForms',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/consent-forms');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch consent forms');
        }
    }
);

// Fetch single consent form by ID
export const fetchConsentForm = createAsyncThunk(
    'consent/fetchConsentForm',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/consent-forms/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch consent form');
        }
    }
);

// Create new consent form
export const createConsentForm = createAsyncThunk(
    'consent/createConsentForm',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post('/consent-forms', formData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to create consent form');
        }
    }
);

// Update consent form
export const updateConsentForm = createAsyncThunk(
    'consent/updateConsentForm',
    async ({ case_id, formData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/consent-forms/case/${case_id}`, formData);
            return response.data.data ?? response.data; // the API already returns { message, data }
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to update consent form');
        }
    }
);

// Delete consent form
export const deleteConsentForm = createAsyncThunk(
    'consent/deleteConsentForm',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/consent-forms/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete consent form');
        }
    }
);

/* ==============================
   SLICE
============================== */

const consentSlice = createSlice({
    name: 'consent',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentConsentForm: (state, action) => {
            state.currentConsentForm = action.payload;
        },
        clearCurrentConsentForm: (state) => {
            state.currentConsentForm = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchConsentForms.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchConsentForms.fulfilled, (state, action) => {
                state.isLoading = false;
                state.consentForms = action.payload;
            })
            .addCase(fetchConsentForms.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchConsentForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchConsentForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentConsentForm = action.payload;
            })
            .addCase(fetchConsentForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createConsentForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createConsentForm.fulfilled, (state, action) => {
                state.isLoading = false;
                if (!action.payload) return;

                state.consentForms.unshift(action.payload);
                // ✅ Ye line lazmi add karein taake current state update ho jaye
                state.currentConsentForm = action.payload;
            })
            .addCase(createConsentForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateConsentForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateConsentForm.fulfilled, (state, action) => {
                state.isLoading = false;

                // ✅ Extract actual data from the response wrapper
                const updatedData = action.payload.data || action.payload;

                if (!updatedData) return;

                // Update list
                const index = state.consentForms.findIndex((f) => f.id === updatedData.id);
                if (index !== -1) {
                    state.consentForms[index] = updatedData;
                }

                // Update current form
                if (state.currentConsentForm?.id === updatedData.id) {
                    state.currentConsentForm = updatedData;
                }
            })
            .addCase(updateConsentForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteConsentForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteConsentForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.consentForms = state.consentForms.filter((f) => f.id !== action.payload);
                if (state.currentConsentForm?.id === action.payload) {
                    state.currentConsentForm = null;
                }
            })
            .addCase(deleteConsentForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentConsentForm, clearCurrentConsentForm } = consentSlice.actions;
export default consentSlice.reducer;
